/* eslint-disable import/extensions */
/* eslint-disable import/no-unresolved */
import React, { useState, useCallback } from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';
import { Stack } from '@fluentui/react';
import {
  Button,
  Caption1Stronger,
  Caption1,
  makeStyles,
  FluentProvider,
  webLightTheme,
  mergeClasses,
  Divider,
  Radio,
  RadioGroup,
} from '@fluentui/react-components';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { Panel } from '@bingads-webui-campaign-react/panel';
import { ComponentSettings, ThemeFluent } from '@bingads-webui-theme-2018/theme-fluent';
import { ThemeProvider } from 'react-jss';
import { Surface } from '@bingads-webui-fluent-component/surface';
import {
  LineChart,
} from '@fluentui/react-charting';
import { LearnMoreLink } from '@bingads-webui-campaign-react/help';
import ReactDOM from 'react-dom';

import { getPanelStyles } from './style/style';
import {
  PERFORMANCE_INSIGHTS_ISSUE,
  PERFORMANCE_INSIGHTS_TYPE,
  PERFORMANCE_INSIGHTS_POPUP_CONFIG,
  PERFORMANCE_INSIGHTS_PANEL_CONTAINER,
} from './constants/consts';

const useStyles = makeStyles({
  inlineChart: {
    width: '500px',
    height: '290px',
  },
});

export const PerformanceInsightsPanel = (props) => {
  const { allData, newI18n, entity } = props;
  const [isPanelOpen, setIsPanelOpen] = useState(true);
  const onCancel = useCallback(() => {
    ReactDOM.unmountComponentAtNode(document.querySelector(PERFORMANCE_INSIGHTS_PANEL_CONTAINER));
    setIsPanelOpen(false);
  }, []);
  const classes = useDefaultStyles(getPanelStyles);
  const getColor = useCallback((metricName) => {
    if (metricName === 'Actual') {
      return 'rgba(227, 0, 140, 1)';
    } else if (metricName === 'CTR') {
      return 'rgba(99, 124, 239, 1)';
    } else if (metricName === 'Impressions') {
      return 'rgba(42, 160, 164, 1)';
    }
    return 'gray';
  }, []);

  const budgetResult = [];
  budgetResult.push({
    legend: 'Actual',
    data: allData[PERFORMANCE_INSIGHTS_ISSUE.BUDGET].actualBudgetPoints,
    color: getColor('Actual'),
    lineOptions: {
      strokeWidth: '2px',
    },
  });
  budgetResult.push({
    legend: 'Estimation',
    legendShape: 'dottedLine',
    data: allData[PERFORMANCE_INSIGHTS_ISSUE.BUDGET].estimatedBudgetPoints,
    color: 'gray',
    lineOptions: {
      strokeDasharray: '2px',
      strokeDashoffset: '-1',
      strokeLinecap: 'butt',
      strokeWidth: '2px',
    },
  });
  const bidResult = [];
  bidResult.push({
    legend: 'Actual',
    data: allData[PERFORMANCE_INSIGHTS_ISSUE.BID].actualBidPoints,
    color: getColor('Actual'),
    lineOptions: {
      strokeWidth: '2px',
    },
  });
  bidResult.push({
    legend: 'Estimation',
    legendShape: 'dottedLine',
    data: allData[PERFORMANCE_INSIGHTS_ISSUE.BID].estimatedBidPoints,
    color: 'gray',
    lineOptions: {
      strokeDasharray: '2px',
      strokeDashoffset: '-1',
      strokeLinecap: 'butt',
      strokeWidth: '2px',
    },
  });
  const budgetChartData = {
    chartTitle: newI18n.getString(
      PERFORMANCE_INSIGHTS_POPUP_CONFIG[PERFORMANCE_INSIGHTS_TYPE.ISSUE][PERFORMANCE_INSIGHTS_ISSUE.BUDGET].chartTitle,
      allData[PERFORMANCE_INSIGHTS_ISSUE.BUDGET]
    ),
    lineChartData: budgetResult,
  };
  const bidChartData = {
    chartTitle: newI18n.getString(
      PERFORMANCE_INSIGHTS_POPUP_CONFIG[PERFORMANCE_INSIGHTS_TYPE.ISSUE][PERFORMANCE_INSIGHTS_ISSUE.BID].chartTitle,
      allData[PERFORMANCE_INSIGHTS_ISSUE.BID]
    ),
    lineChartData: bidResult,
  };
  const chartClasses = useStyles();
  const colorFillBarData = [
    {
      legend: 'Time range 1',
      color: '#EEACB2',
      data: [
        {
          startX: allData[PERFORMANCE_INSIGHTS_ISSUE.BUDGET].actualBudgetPoints[5].x,
          endX: allData[PERFORMANCE_INSIGHTS_ISSUE.BUDGET].actualBudgetPoints[6].x,
        },
      ],
    },
  ];
  return (
    <Panel
      isOpen={isPanelOpen}
      onClose={onCancel}
      i18n={newI18n}
      panelClassName="side-panel-performance-insights"
      title={<Caption1Stronger>{newI18n.getString(_TL_('Insights of {{campaignName}}'), { campaignName: entity.Name })}</Caption1Stronger>}
      className={classes}
    >
      <ThemeProvider theme={{ components: ComponentSettings, core: ThemeFluent }} >
        <FluentProvider theme={webLightTheme} className={classes.flexContainer} >
          <Stack>
            <Stack.Item>
              <Caption1>
                {
                  newI18n.getString(_TL_('{{campaignName}}\'s clicks are dropping. To revert the negative trend, you\'re highly recommended to fix all health check issues to unblock campaign performance. You can follow below instructions to fix it now.'), { campaignName: entity.Name })
                }
              </Caption1>
            </Stack.Item>
            <Stack.Item>
              <Surface
                key="main-form"
                type="main-form"
              >
                <Stack>
                  <Stack.Item>
                    <Caption1Stronger>
                      {
                        newI18n.getString(PERFORMANCE_INSIGHTS_POPUP_CONFIG[PERFORMANCE_INSIGHTS_TYPE.ISSUE][PERFORMANCE_INSIGHTS_ISSUE.BUDGET].title)
                      }
                    </Caption1Stronger>
                  </Stack.Item>
                  <Stack.Item>
                    <Stack horizontal>
                      <Stack.Item>
                        <LineChart
                          data={budgetChartData}
                          legendsOverflowText="Overflow Items"
                          className={mergeClasses('inline-chart', chartClasses.inlineChart)}
                          allowMultipleShapesForPoints
                          tickFormat="%m/%d"
                          tickValues={_.map(allData[PERFORMANCE_INSIGHTS_ISSUE.BUDGET].actualBudgetPoints, point => point.x)}
                          colorFillBars={colorFillBarData}
                        />
                      </Stack.Item>
                      <Stack.Item>
                        <Surface
                          key="support-main"
                          type="support-main"
                        >
                          <Stack>
                            <Stack.Item>
                              <Caption1>
                                {
                                  newI18n.getString(PERFORMANCE_INSIGHTS_POPUP_CONFIG[PERFORMANCE_INSIGHTS_TYPE.ISSUE][PERFORMANCE_INSIGHTS_ISSUE.BUDGET].description, allData[PERFORMANCE_INSIGHTS_ISSUE.BUDGET])
                                }
                              </Caption1>
                            </Stack.Item>
                            <Stack.Item>
                              <Caption1>
                                {
                                  newI18n.getString(PERFORMANCE_INSIGHTS_POPUP_CONFIG[PERFORMANCE_INSIGHTS_TYPE.ISSUE][PERFORMANCE_INSIGHTS_ISSUE.BUDGET].pitching)
                                }
                              </Caption1>
                            </Stack.Item>
                            <Stack.Item>
                              <Divider />
                            </Stack.Item>
                            <Stack.Item>
                              <Caption1Stronger>{newI18n.getString(_TL_('What you can do next:'))}</Caption1Stronger>
                            </Stack.Item>
                            <Stack.Item>
                              <Caption1>
                                {
                                  newI18n.getString(PERFORMANCE_INSIGHTS_POPUP_CONFIG[PERFORMANCE_INSIGHTS_TYPE.ISSUE][PERFORMANCE_INSIGHTS_ISSUE.BUDGET].actionDetails[0], allData[PERFORMANCE_INSIGHTS_ISSUE.BUDGET])
                                }
                              </Caption1>
                            </Stack.Item>
                            <Stack.Item>
                              <Stack horizontal>
                                <Stack.Item>
                                  <Button appearance="primary" onClick={onCancel}>{newI18n.getString('Save changes')}</Button>
                                </Stack.Item>
                                <Stack.Item>
                                  <LearnMoreLink
                                    linkText={newI18n.getString(_TL_('Learn more'))}
                                    topic="app54823"
                                  />
                                </Stack.Item>
                              </Stack>
                            </Stack.Item>
                          </Stack>
                        </Surface>
                      </Stack.Item>
                    </Stack>
                  </Stack.Item>
                </Stack>
              </Surface>
            </Stack.Item>
            <Stack.Item>
              <Surface
                key="main-form"
                type="main-form"
              >
                <Stack>
                  <Caption1Stronger>{newI18n.getString(PERFORMANCE_INSIGHTS_POPUP_CONFIG[PERFORMANCE_INSIGHTS_TYPE.ISSUE][PERFORMANCE_INSIGHTS_ISSUE.BID].title)}</Caption1Stronger>
                  <Stack horizontal>
                    <Stack.Item>
                      <LineChart
                        data={bidChartData}
                        legendsOverflowText="Overflow Items"
                        className={mergeClasses('inline-chart', chartClasses.inlineChart)}
                        allowMultipleShapesForPoints
                        tickFormat="%m/%d"
                        tickValues={_.map(allData[PERFORMANCE_INSIGHTS_ISSUE.BID].actualBidPoints, point => point.x)}
                      />
                    </Stack.Item>
                    <Stack.Item>
                      <Surface
                        key="support-main"
                        type="support-main"
                      >
                        <Stack>
                          <Caption1>{newI18n.getString(PERFORMANCE_INSIGHTS_POPUP_CONFIG[PERFORMANCE_INSIGHTS_TYPE.ISSUE][PERFORMANCE_INSIGHTS_ISSUE.BID].description, allData[PERFORMANCE_INSIGHTS_ISSUE.BID])}</Caption1>
                          <Caption1>{newI18n.getString(PERFORMANCE_INSIGHTS_POPUP_CONFIG[PERFORMANCE_INSIGHTS_TYPE.ISSUE][PERFORMANCE_INSIGHTS_ISSUE.BID].pitching)}</Caption1>
                          <Divider />
                          <Caption1Stronger>{newI18n.getString(_TL_('What you can do next:'))}</Caption1Stronger>
                          <RadioGroup value="first">
                            <Radio value="first" label={newI18n.getString(PERFORMANCE_INSIGHTS_POPUP_CONFIG[PERFORMANCE_INSIGHTS_TYPE.ISSUE][PERFORMANCE_INSIGHTS_ISSUE.BID].actionDetails[0], allData[PERFORMANCE_INSIGHTS_ISSUE.BID])} />
                            <Radio value="second" label={newI18n.getString(PERFORMANCE_INSIGHTS_POPUP_CONFIG[PERFORMANCE_INSIGHTS_TYPE.ISSUE][PERFORMANCE_INSIGHTS_ISSUE.BID].actionDetails[1], allData[PERFORMANCE_INSIGHTS_ISSUE.BID])} />
                          </RadioGroup>
                          <Stack horizontal>
                            <Button appearance="primary" onClick={onCancel}>{newI18n.getString('Save changes')}</Button>
                            <LearnMoreLink
                              linkText={newI18n.getString(_TL_('Learn more'))}
                              topic="app54823"
                            />
                          </Stack>
                        </Stack>
                      </Surface>
                    </Stack.Item>
                  </Stack>
                </Stack>
              </Surface>
            </Stack.Item>
          </Stack>
        </FluentProvider>
      </ThemeProvider>
    </Panel>
  );
};
PerformanceInsightsPanel.propTypes = {
  allData: PropTypes.shape({}).isRequired,
  newI18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  entity: PropTypes.shape({
    Name: PropTypes.string,
  }).isRequired,
};
